<template>
  <div class="page-container">
    <div class="header-row">
      <div class="title-with-icon">
        <div :class="['operation-icon-box', config.color]">
          <img v-if="config.useImage" :src="config.imageSrc" alt="icon" class="operation-img-icon" />
          <LucideIcon v-else :size="24">
            <g v-html="config.iconPaths"></g>
          </LucideIcon>
        </div>
        <h2 class="page-title">{{ config.title }}</h2>
      </div>
      <button class="btn-primary" @click="openForm">
        <LucideIcon :size="16" class="mr-2">
          <path d="M5 12h14"/><path d="M12 5v14"/>
        </LucideIcon>
        {{ $t('household.operations.createBtn') }} {{ config.title.toLowerCase() }}
      </button>
    </div>

    <!-- Фильтры -->
    <div class="filter-card simple">
      <div class="status-filters">
        <button
          v-for="s in statuses"
          :key="s.id"
          :class="['filter-btn', { active: filterStatus === s.id }]"
          @click="filterStatus = s.id"
        >
          {{ s.label }}
        </button>
      </div>
    </div>

    <!-- Список операций -->
    <div class="operations-list">
      <div v-for="op in filteredOperations" :key="op.id" class="operation-card">
        <div class="op-card-header">
          <div class="op-main-info">
            <div class="op-title-row">
              <h3 class="op-id">{{ $t('household.operations.opTitle') }}{{ op.id }}</h3>
              <span :class="['op-status-badge', op.status]">
                {{ getStatusLabel(op.status) }}
              </span>
            </div>
            <div class="op-meta-info">
              <p>{{ $t('household.operations.datePrefix') }} {{ formatDate(op.date) }}</p>
              <p>{{ $t('household.operations.warehousePrefix') }} {{ getWarehouseName(op.warehouseId) }}</p>
              <p v-if="getFirstZoneName(op)">{{ $t('household.operations.zonePrefix') }} {{ getFirstZoneName(op) }}</p>
              <p v-if="op.counterpartyId">{{ $t('household.operations.counterpartyPrefix') }} {{ getCounterpartyName(op.counterpartyId) }}</p>
            </div>
          </div>
          <div class="op-summary text-right">
            <p class="op-total">{{ formatCurrency(getTotalCost(op)) }}</p>
            <p class="op-positions">{{ op.items.length }} {{ $t('household.operations.positionsSuffix') }}</p>
          </div>
        </div>

        <div v-if="op.notes" class="op-notes">
          {{ op.notes }}
        </div>

        <!-- Товары -->
        <div class="op-items-list">
          <div v-for="item in op.items" :key="item.id" class="op-item-row">
            <div class="op-item-info">
              <p class="op-item-name">{{ getNomenclatureName(item.nomenclatureId) }}</p>
              <div class="op-item-details">
                <span>{{ $t('household.operations.qtyPrefix') }} {{ item.quantity }} {{ getNomenclatureUnit(item.nomenclatureId) }}</span>
                <span v-if="item.batchNumber">{{ $t('household.operations.batchPrefix') }} {{ item.batchNumber }}</span>
                <span v-if="item.location" class="location-mono">
                  {{ item.location.zone }}-{{ item.location.row }}-{{ item.location.rack }}-{{ item.location.level }}
                </span>
                <template v-if="type === 'movement' && item.locationTo">
                  <LucideIcon :size="12" class="mx-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></LucideIcon>
                  <span class="location-mono">
                    {{ item.locationTo.zone }}-{{ item.locationTo.row }}-{{ item.locationTo.rack }}-{{ item.locationTo.level }}
                  </span>
                </template>
              </div>
            </div>
            <div class="op-item-cost text-right">
              <p class="it-cost">{{ formatCurrency(item.cost) }}</p>
              <p class="it-price">{{ formatCurrency(item.cost / item.quantity) }} {{ $t('household.operations.perUnit') }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-if="filteredOperations.length === 0" class="empty-state-card">
        <LucideIcon :size="48" class="empty-icon">
          <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>
        </LucideIcon>
        <p>{{ $t('household.operations.empty') }}</p>
        <button class="link-btn" @click="openForm">{{ $t('household.operations.createFirst') }}</button>
      </div>
    </div>

    <!-- Форма -->
    <OperationForm 
      v-if="isFormOpen" 
      :type="type" 
      @close="isFormOpen = false" 
      @saved="onSaved"
    />
  </div>
</template>

<script>
import LucideIcon from '@/components/common/LucideIcon.vue';
import OperationForm from './OperationForm.vue';
import { getOperations } from '@/api/household_operations';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses, getWarehouseZones } from '@/api/household_warehouses';
import { getCounterparties } from '@/api/household_counterparties';

const IconReceipt = '<path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/>';
const IconConsumption = '<path d="M12 3v14"/><path d="m6 9 6-6 6 6"/><path d="M19 21H5"/>';
const IconMovement = '<path d="m9 18-3-3 3-3"/><path d="m15 6 3 3-3 3"/><path d="M18 9H6a2 2 0 0 0-2 2v2"/><path d="M4 15h12a2 2 0 0 0 2-2v-2"/><path d="m9 18 3 3-3 3"/><path d="m15 6-3-3 3-3"/>';
const IconWriteoff = '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>';
const IconInventory = '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/>';

export default {
  name: 'HouseholdOperationsView',
  components: { LucideIcon, OperationForm },
  data() {
    return {
      operations: [],
      nomenclatures: [],
      warehouses: [],
      zones: [],
      counterparties: [],
      filterStatus: 'all',
      isFormOpen: false,
    };
  },
  computed: {
    statuses() {
      return [
        { id: 'all', label: this.$t('household.shared.all') },
        { id: 'completed', label: this.$t('household.opStatus.completedPlural') },
        { id: 'draft', label: this.$t('household.opStatus.draftPlural') },
      ];
    },
    configMap() {
      return {
        receipt: { title: this.$t('household.operationTypes.receipt'), iconPaths: IconReceipt, color: 'bg-green' },
        consumption: { title: this.$t('household.operationTypes.consumption'), iconPaths: IconConsumption, color: 'bg-blue' },
        movement: { title: this.$t('household.operationTypes.movement'), useImage: true, imageSrc: require('@/assets/images/transport.svg'), color: 'bg-purple' },
        writeoff: { title: this.$t('household.operationTypes.writeoff'), iconPaths: IconWriteoff, color: 'bg-red' },
        inventory: { title: this.$t('household.operationTypes.inventory'), iconPaths: IconInventory, color: 'bg-orange' },
      };
    },
    type() {
      return this.$route.meta.type || 'receipt';
    },
    config() {
      return this.configMap[this.type] || this.configMap.receipt;
    },
    filteredOperations() {
      return this.operations
        .filter(op => this.filterStatus === 'all' || op.status === this.filterStatus)
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    }
  },
  async created() {
    await this.loadData();
  },
  watch: {
    '$route.path': 'loadData'
  },
  methods: {
    async loadData() {
      const typeMap = {
        receipt: 'add',
        consumption: 'expense',
        movement: 'move',
        writeoff: 'cancellation',
        inventory: 'inventory'
      };

      const [opRes, nomRes, whRes, cpRes] = await Promise.all([
        getOperations({ operation_type: typeMap[this.type] }),
        getNomenclatures(),
        getWarehouses(),
        getCounterparties()
      ]);
      
      this.operations = (Array.isArray(opRes.data) ? opRes.data : (opRes.data?.results || [])).map(op => ({
        ...op,
        type: this.type, // Keep local type for consistency
        warehouseId: op.warehouse,
        counterpartyId: op.counterparty,
        zoneId: op.zone_id || op.zone,
        items: (op.nomenclatures || []).map(it => ({
          ...it,
          nomenclatureId: it.nomenclature,
          batchNumber: it.batch,
          cost: it.cost || 0
        }))
      }));

      this.nomenclatures = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
      this.warehouses = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
      this.counterparties = Array.isArray(cpRes.data) ? cpRes.data : (cpRes.data?.results || []);
      
      // Load zones for all warehouses
      const allZones = [];
      for (const wh of this.warehouses) {
        try {
          const zonesRes = await getWarehouseZones({ warehouse: wh.id });
          const zonesData = Array.isArray(zonesRes.data) ? zonesRes.data : (zonesRes.data?.results || []);
          allZones.push(...zonesData.map(z => ({ ...z, warehouseId: wh.id })));
        } catch (e) {
          // Ignore zone loading errors
        }
      }
      this.zones = allZones;
    },
    formatDate(d) {
      return new Date(d).toLocaleDateString('ru-RU');
    },
    formatCurrency(v) {
      return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT' }).format(v);
    },
    getStatusLabel(s) {
      const map = {
        completed: this.$t('household.opStatus.completed'),
        draft: this.$t('household.opStatus.draft'),
        cancelled: this.$t('household.opStatus.canceled')
      };
      return map[s] || s;
    },
    getWarehouseName(id) {
      return this.warehouses.find(w => w.id === id)?.name || id;
    },
    getCounterpartyName(id) {
      return this.counterparties.find(c => c.id === id)?.name || id;
    },
    getFirstZoneName(op) {
      // Zone can be in op.nomenclatures (original field) or op.items (mapped field)
      const firstItem = (op.nomenclatures && op.nomenclatures[0]) || (op.items && op.items[0]);
      if (firstItem?.zone && typeof firstItem.zone === 'object') {
        return firstItem.zone.name || `${this.$t('household.shared.zoneLabel')} ${firstItem.zone.id}`;
      }
      return '';
    },
    getZoneName(zoneId, warehouseId) {
      const zone = this.zones.find(z => z.id === zoneId);
      return zone?.name || `${this.$t('household.shared.zoneLabel')} ${zoneId}`;
    },
    getNomenclatureName(id) {
      return this.nomenclatures.find(n => n.id === id)?.name || id;
    },
    getNomenclatureUnit(id) {
      return this.nomenclatures.find(n => n.id === id)?.unit || '';
    },
    getTotalCost(op) {
      return op.items.reduce((sum, it) => sum + it.cost, 0);
    },
    openForm() {
      this.isFormOpen = true;
    },
    onSaved() {
      this.isFormOpen = false;
      this.loadData();
    }
  }
};
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.header-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; }

.title-with-icon { display: flex; align-items: center; gap: 1.25rem; }
.operation-icon-box {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}
.bg-green { background: #22c55e; }
.bg-blue { background: #3b82f6; }
.bg-purple { background: #a855f7; }
.bg-red { background: #ef4444; }
.bg-orange { background: #f97316; }
.operation-img-icon { width: 24px; height: 24px; filter: brightness(0) invert(1); }

.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }

.filter-card.simple { 
  background: white; 
  border: 1px solid #e2e8f0; 
  border-radius: 1rem; 
  padding: 0.5rem; 
  margin-bottom: 2rem; 
  display: inline-flex;
}
.status-filters { display: flex; gap: 0.25rem; }
.filter-btn {
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  padding: 0.625rem 1.25rem;
  color: #64748b;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}
.filter-btn:hover { background: #f8fafc; color: #030229; }
.filter-btn.active { background: #3b82f6; color: #fff; }

.operations-list { display: flex; flex-direction: column; gap: 1.5rem; }
.operation-card { 
  background: white; 
  border: 1px solid #e2e8f0; 
  border-radius: 1.25rem; 
  padding: 2rem; 
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.2s;
}
.operation-card:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.05); }

.op-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; }
.op-title-row { display: flex; align-items: center; gap: 1rem; margin-bottom: 0.75rem; }
.op-id { color: #030229; font-weight: 700; margin: 0; font-size: 1.25rem; }
.op-status-badge { padding: 0.35rem 0.75rem; border-radius: 2rem; font-size: 0.7rem; font-weight: 600; }
.op-status-badge.completed { background: #dcfce7; color: #15803d; }
.op-status-badge.draft { background: #f1f5f9; color: #64748b; }

.op-meta-info p { color: #64748b; font-size: 0.875rem; margin: 0.35rem 0 0; }

.op-total { color: #030229; font-weight: 800; font-size: 1.25rem; margin: 0; }
.op-positions { color: #94a3b8; font-size: 0.8rem; margin: 0.25rem 0 0; }

.op-notes { background: #f8fafc; border-radius: 0.75rem; padding: 1rem; margin-bottom: 1.5rem; color: #475569; font-size: 0.875rem; font-style: italic; border-left: 4px solid #e2e8f0; }

.op-items-list { border-top: 1px solid #f1f1f4; padding-top: 1.5rem; display: flex; flex-direction: column; gap: 0.75rem; }
.op-item-row { background: #fafafa; padding: 1rem 1.25rem; border-radius: 1rem; display: flex; justify-content: space-between; align-items: center; border: 1px solid #f1f1f4; }

.op-item-name { color: #030229; font-weight: 600; margin: 0; font-size: 1rem; }
.op-item-details { display: flex; align-items: center; gap: 1.25rem; color: #64748b; font-size: 0.8rem; margin-top: 0.35rem; }
.location-mono { font-family: monospace; color: #2563eb; background: #eff6ff; padding: 0.125rem 0.35rem; border-radius: 0.25rem; }

.it-cost { color: #030229; font-weight: 600; font-size: 1rem; margin: 0; }
.it-price { color: #94a3b8; font-size: 0.75rem; margin: 0.125rem 0 0; }

.empty-state-card { text-align: center; padding: 5rem 2rem; background: white; border: 1px dashed #e2e8f0; border-radius: 1.25rem; }
.empty-icon { color: #cbd5e1; margin-bottom: 1.5rem; }
.empty-state-card p { color: #64748b; font-size: 1.1rem; margin-bottom: 1.5rem; }
.link-btn { background: #eff6ff; border: none; color: #2563eb; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.link-btn:hover { background: #dbeafe; }

.text-right { text-align: right; }
.btn-primary { 
  background: #2563eb; 
  color: #fff; 
  border: none; 
  border-radius: 0.75rem; 
  padding: 0.75rem 1.5rem; 
  font-weight: 600; 
  cursor: pointer; 
  display: flex; 
  align-items: center;
  transition: all 0.2s;
}
.btn-primary:hover { background: #1d4ed8; transform: translateY(-1px); box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); }
.mr-2 { margin-right: 0.5rem; }
.mx-1 { margin-left: 0.25rem; margin-right: 0.25rem; }
</style>
